import {
  IAutocompleteItem,
  ICollection,
  IMcpTool,
  ITool,
  Mention,
  Tools,
} from "@/types";
import { QueryObserver, useQueryClient, type QueryKey } from "@tanstack/react-query";
import {
  useComposerSession,
  useComposerToolsQuery,
  useComposerAutocompleteQuery,
  useComposerCollectionsQuery,
  isComposerItemCurrent,
} from "@/features/playground/hooks/useComposerQueries";
import {
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useId,
} from "react";
import { MenuSelection } from "./menu-list";
import { SendIcon } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";
import ToolIcon from "./agent-tool-icons";
import { useMessagesStore } from "@/atoms/playground";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { useStoredTheme } from "@/utils/theme-context";
import { useThemeMode } from "@/contexts/ThemeContext";
import { usePlaygroundPresentation } from "../playground-presentation";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import { IDocument } from "@/types/conversations";

// Local to the picker: a connected node can still be hidden by a responsive ancestor.
const isEligiblePickerFocusTarget = (element: HTMLElement | null): element is HTMLElement => {
  if (!element || element.ownerDocument !== document || !element.isConnected ||
    element.matches(":disabled") || element.getAttribute("aria-disabled") === "true" ||
    element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  if (!(element instanceof HTMLButtonElement) && element.getAttribute("contenteditable") !== "true") return false;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden" ||
      style.visibility === "collapse" || style.contentVisibility === "hidden") return false;
  }
  return Array.from(element.getClientRects()).some(rect => rect.width > 0 && rect.height > 0);
};

const formatMentionForApi = (mention: Mention): string => {
  if (mention.type === "document") {
    return `@document[${mention.collectionId}]`;
  }
  if (mention.type === "collection") {
    return `@collection[${mention.collectionId}]`;
  }
  if (mention.collectionId) {
    return `@${mention.toolId}[${mention.collectionId}]`;
  }
  return `@${mention.toolId}`;
};
export interface EditableContentRef {
  openMenu: (anchorRect?: DOMRect, trigger?: HTMLButtonElement) => void;
  removeDocumentMention: (documentId: string) => void;
}

const isReadyDocument = (document: IDocument) => document.status === "complete";

const isMcpConnection = (tool: ITool | null | undefined): boolean =>
  tool?.type === "mcp";

const filterMcpTools = (tool: ITool | null, searchQuery: string): IMcpTool[] => {
  if (!isMcpConnection(tool) || !tool?.tools?.length) return [];
  const query = searchQuery.trim().toLowerCase();
  if (!query) return tool.tools;
  return tool.tools.filter(
    (mcpTool) =>
      mcpTool.name.toLowerCase().includes(query) ||
      mcpTool.description?.toLowerCase().includes(query),
  );
};

const isDocumentTool = (tool: ITool | null | undefined) => {
  if (!tool) return false;

  return (
    tool.id.toLowerCase() === "document" ||
    tool.name.trim().toLowerCase() === "document"
  );
};

const getReadyDocumentKeys = (documents: IDocument[] | undefined) => {
  return (documents || []).filter(isReadyDocument).reduce(
    (acc, document) => {
      acc.ids.add(document.id);
      acc.names.add(document.name.trim().toLowerCase());
      acc.names.add(document.title.trim().toLowerCase());
      return acc;
    },
    { ids: new Set<string>(), names: new Set<string>() },
  );
};

const isReadyDocumentAutocompleteItem = (
  item: IAutocompleteItem | ICollection,
  readyDocuments: ReturnType<typeof getReadyDocumentKeys>,
) => {
  return (
    readyDocuments.ids.has(item.id) ||
    readyDocuments.names.has(item.name.trim().toLowerCase())
  );
};

const filterAvailableTools = (tools: Tools | undefined) => {
  if (!tools?.length) return [];
  return tools;
};

const filterReadyDocumentAutocompleteItems = (
  items: IAutocompleteItem[] | undefined,
  documents: IDocument[] | undefined,
) => {
  if (!items?.length) return [];

  const readyDocuments = getReadyDocumentKeys(documents);

  return items.filter(
    (item) =>
      item.type !== "document" ||
      item.source !== "conversation" ||
      isReadyDocumentAutocompleteItem(item, readyDocuments),
  );
};

const filterReadyDocumentCollections = (
  items: ICollection[] | undefined,
  selectedTool: ITool | null,
  documents: IDocument[] | undefined,
) => {
  if (!items?.length) return [];
  if (!isDocumentTool(selectedTool)) return items;

  const readyDocuments = getReadyDocumentKeys(documents);
  const trackedIds = new Set((documents || []).map((d) => d.id));
  const trackedNames = new Set(
    (documents || []).flatMap((d) =>
      [d.name, d.title].map((s) => s.trim().toLowerCase()),
    ),
  );

  return items.filter(
    (item) =>
      isReadyDocumentAutocompleteItem(item, readyDocuments) ||
      (!trackedIds.has(item.id) &&
        !trackedNames.has(item.name.trim().toLowerCase())),
  );
};

export const EditableContent = forwardRef<
  EditableContentRef,
  {
    handleFormSubmit: (
      messageOverride?: string,
      mentionsOverride?: Mention[],
    ) => void;
    onTextChange: (message: string) => void;
    conversationId?: string;
    isStreaming?: boolean;
    isUploading?: boolean;
    isSubmitDisabled?: boolean;
    documents?: IDocument[];
    errorMessageId?: string;
    invalid?: boolean;
    menuId?: string;
    onMenuOpenChange?: (open: boolean) => void;
    stop?: () => void;
  }
>(
  (
    {
      handleFormSubmit,
      onTextChange,
      conversationId,
      isStreaming,
      isUploading,
      isSubmitDisabled,
      documents,
      errorMessageId,
      invalid,
      menuId,
      onMenuOpenChange,
      stop,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { mode } = useThemeMode();
    const { modern, mobile } = usePlaygroundPresentation();
    const compactDesktop = modern && !mobile;
    const storedTheme = useStoredTheme();
    const primaryButton = storedTheme.button.primary;
    const session = useComposerSession(conversationId);
    const { isCurrent: isSessionCurrent, identity: scopeIdentity } = session;
    const queryClient = useQueryClient();
    const toolsQuery = useComposerToolsQuery();
    const { data: tools, isLoading } = toolsQuery;

    const [position, setPosition] = useState<{
      top?: number;
      left: number;
      bottom?: number;
    }>({ left: 0 });
    const [filteredTools, setFilteredTools] = useState<Tools>([]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [focusPickerSearch, setFocusPickerSearch] = useState(false);
    const [menuType, setMenuType] = useState<"tools" | "collections">("tools");
    const [tool, setTool] = useState<ITool | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { pushReference } = useMessagesStore();
    const [searchQuery, setSearchQuery] = useState("");
    const availableTools = useMemo(() => filterAvailableTools(tools), [tools]);
    const autocompleteQuery = useComposerAutocompleteQuery(session, searchQuery, tool === null);
    const { data: autocompleteData, isLoading: isAutocompleteLoading } = autocompleteQuery;
    const referenceScopeIdentity = useMessagesStore(state => state.conversationScopeIdentity);
    const canWriteReferences = useCallback(() => isSessionCurrent() && (
      useMessagesStore.getState().conversationScopeIdentity === scopeIdentity ||
      // New-chat and confidential sessions intentionally leave this store
      // unowned. The canonical editor session still owns these query results.
      useMessagesStore.getState().conversationScopeIdentity === null
    ), [isSessionCurrent, scopeIdentity]);

    const readyAutocompleteData = useMemo(
      () => filterReadyDocumentAutocompleteItems(autocompleteData, documents),
      [autocompleteData, documents],
    );

    useEffect(() => {
      if (!canWriteReferences() || !readyAutocompleteData.length) return;

      const references = readyAutocompleteData.reduce(
        (acc: any, item: IAutocompleteItem) => {
          return {
            ...acc,
            [item.type]: {
              ...(acc[item.type] || {}),
              [item.id]: item,
            },
          };
        },
        {},
      );
      pushReference(references);
    }, [pushReference, readyAutocompleteData, canWriteReferences, referenceScopeIdentity]);

    const collectionsQuery = useComposerCollectionsQuery(session, tool?.id, searchQuery, !!tool && !isMcpConnection(tool));
    const {
      data: collectionsData,
      isLoading: collectionsLoading,
      isFetching: documentsFetching,
    } = collectionsQuery;

    const readyCollectionsData = useMemo(
      () => filterReadyDocumentCollections(collectionsData, tool, documents),
      [collectionsData, tool, documents],
    );

    // Tools exposed by the currently drilled-into MCP connection, filtered by
    // the text typed after "@" so the user can mention one by name.
    const mcpToolItems = useMemo(
      () => filterMcpTools(tool, searchQuery),
      [tool, searchQuery],
    );

    useEffect(() => {
      if (!canWriteReferences() || !readyCollectionsData.length) return;

      const references = readyCollectionsData.reduce(
        (acc: any, item: ICollection | IAutocompleteItem) => {
          const itemType =
            "type" in item ? item.type : tool?.type || "collection";

          return {
            ...acc,
            [itemType]: {
              ...(acc[itemType] || {}),
              [item.id]: item,
            },
          };
        },
        {},
      );
      pushReference(references);
    }, [pushReference, readyCollectionsData, tool, canWriteReferences, referenceScopeIdentity]);
    useEffect(() => {
      setSelectedIndex(0);
    }, [
      filteredTools,
      readyCollectionsData,
      isMenuOpen,
      menuType,
      readyAutocompleteData,
      mcpToolItems,
    ]);

    useEffect(() => {
      if (menuType === "tools") {
        setFilteredTools(availableTools);
      }
    }, [availableTools, menuType]);

    const editorRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const defaultMenuId = useId();
    const menuTrigger = useRef<{ element: HTMLButtonElement; token: string } | null>(null);
    const currentPicker = useRef({ token: session.token, open: isMenuOpen });
    useLayoutEffect(() => {
      currentPicker.current = { token: session.token, open: isMenuOpen };
      onMenuOpenChange?.(isMenuOpen && session.ready);
    }, [isMenuOpen, session.token, session.ready, onMenuOpenChange]);
    const isComposingRef = useRef(false);
    const savedRange = useRef<Range | null>(null);
    const previousReadyDocumentIdsRef = useRef<Set<string>>(new Set());
    const [editorSession, setEditorSession] = useState(session.token);
    const mentionEligibility = useRef(new WeakMap<HTMLElement, () => boolean>());
    const retainedSelections = useRef(new Map<string, () => void>());
    const releaseSelections = () => {
      retainedSelections.current.forEach(unsubscribe => unsubscribe());
      retainedSelections.current.clear();
    };
    useLayoutEffect(() => releaseSelections, []);
    const isEditorCurrent = useCallback(() => isSessionCurrent() && editorSession === session.token,
      [isSessionCurrent, editorSession, session.token]);
    const pickerIsError = toolsQuery.isError || (isMcpConnection(tool) ? false : tool ? collectionsQuery.isError : searchQuery ? autocompleteQuery.isError : false);
    const pickerIsLoading = !isMcpConnection(tool) && (isLoading || collectionsLoading || documentsFetching || isAutocompleteLoading);
    const pickerItems = menuType === "tools" ? filteredTools : isMcpConnection(tool) ? mcpToolItems : tool ? readyCollectionsData : readyAutocompleteData;
    const isMenuItemSelectable = (item: { id: string; selectable?: boolean }) => {
      if (!session.ready || editorSession !== session.token || pickerIsError || pickerIsLoading || item.selectable === false) return false;
      if (menuType === "tools") return isComposerItemCurrent(queryClient, toolsQuery.queryKey, item);
      if (tool && !isComposerItemCurrent(queryClient, toolsQuery.queryKey, tool)) return false;
      if (isMcpConnection(tool)) {
        return !!queryClient.getQueryData<Tools>(toolsQuery.queryKey)?.find(candidate => candidate.id === tool?.id)?.tools?.some(candidate => candidate.id === item.id);
      }
      return isComposerItemCurrent(queryClient, tool ? collectionsQuery.queryKey : autocompleteQuery.queryKey, item);
    };
    // Reset menu state in the transition render, before old options can commit.
    if (editorSession !== session.token) {
      setEditorSession(session.token);
      setIsMenuOpen(false);
      setFocusPickerSearch(false);
      setMenuType("tools");
      setTool(null);
      setSearchQuery("");
      setFilteredTools([]);
    }
    const previousEditorSession = useRef(session.token);
    useLayoutEffect(() => {
      if (previousEditorSession.current === session.token) return;
      previousEditorSession.current = session.token;
      savedRange.current = null;
      menuTrigger.current = null;
      isComposingRef.current = false;
      releaseSelections();
      previousReadyDocumentIdsRef.current = new Set();
      // Preserve user-entered text, but drop stale pills including their labels.
      // Labels may themselves contain reference syntax; copying or escaping them
      // cannot establish safety without a downstream parser escape contract.
      const editor = editorRef.current;
      editor?.querySelectorAll<HTMLElement>("[data-mention-tool-id]").forEach(element => {
        element.remove();
      });
      if (editor) onTextChange(editor.textContent || "");
    }, [session.token, onTextChange]);

    const removeDocumentMention = (documentId: string) => {
      if (!editorRef.current) return;

      const mentionElements = Array.from(
        editorRef.current.querySelectorAll<HTMLElement>(
          "[data-mention-collection-id]",
        ),
      );

      let removedMention = false;
      mentionElements.forEach((mentionElement) => {
        if (mentionElement.dataset.mentionCollectionId === documentId) {
          mentionElement.remove();
          removedMention = true;
        }
      });

      if (removedMention && !editorRef.current.textContent?.trim()) {
        editorRef.current.innerHTML = "";
      }
    };

    useEffect(() => {
      if (!editorRef.current) return;

      const readyDocumentIds = getReadyDocumentKeys(documents).ids;
      const previousReadyDocumentIds = previousReadyDocumentIdsRef.current;
      const mentionElements = Array.from(
        editorRef.current.querySelectorAll<HTMLElement>(
          "[data-mention-collection-id]",
        ),
      );

      let removedMention = false;
      mentionElements.forEach((mentionElement) => {
        const documentId = mentionElement.dataset.mentionCollectionId;
        const mentionType = mentionElement.dataset.mentionType;
        const mentionToolId = mentionElement.dataset.mentionToolId;
        const mentionToolName = mentionElement.dataset.mentionToolName;
        const isDocumentMention =
          mentionType === "document" ||
          mentionToolId === "document" ||
          mentionToolName?.trim().toLowerCase() === "document" ||
          Boolean(
            documentId &&
            previousReadyDocumentIds.has(documentId) &&
            !readyDocumentIds.has(documentId),
          );

        if (
          documentId &&
          isDocumentMention &&
          !readyDocumentIds.has(documentId)
        ) {
          mentionElement.remove();
          removedMention = true;
        }
      });

      if (removedMention && !editorRef.current.textContent?.trim()) {
        editorRef.current.innerHTML = "";
      }

      previousReadyDocumentIdsRef.current = readyDocumentIds;
    }, [documents]);

    const saveSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorRef.current?.contains(range.commonAncestorContainer)) {
          savedRange.current = range.cloneRange();
        }
      }
    };

    const closeMenu = useCallback((restoreFocus = false) => {
      if (!isEditorCurrent()) return;
      const target = menuTrigger.current;
      menuTrigger.current = null;
      setIsMenuOpen(false);
      setFocusPickerSearch(false);
      setMenuType("tools");
      setSearchQuery("");
      setTool(null);
      if (!restoreFocus) return;
      if (target?.token === session.token && isEligiblePickerFocusTarget(target.element)) {
        target.element.focus();
      } else if (isEligiblePickerFocusTarget(editorRef.current)) {
        editorRef.current.focus();
      }
    }, [isEditorCurrent, session.token]);

    useImperativeHandle(ref, () => ({
      removeDocumentMention,
      openMenu: (anchorRect?: DOMRect, trigger?: HTMLButtonElement) => {
        if (!isEditorCurrent()) return;
        if (isMenuOpen && trigger && menuTrigger.current?.element === trigger) {
          closeMenu(true);
          return;
        }
        menuTrigger.current = trigger ? { element: trigger, token: session.token } : null;
        setFocusPickerSearch(Boolean(trigger));
        if (editorRef.current) {
          editorRef.current.focus();
          saveSelection();
          if (!savedRange.current) {
            const range = document.createRange();
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            savedRange.current = range;
          }

          if (anchorRect) {
            const MENU_WIDTH = 220; // Approximate width including padding
            const screenWidth = window.innerWidth;
            let leftPos = anchorRect.left;

            // Check right edge overflow
            if (leftPos + MENU_WIDTH > screenWidth) {
              leftPos = Math.max(10, screenWidth - MENU_WIDTH - 10);
            }

            setPosition({
              bottom: window.innerHeight - anchorRect.top + 10, // 10px padding
              left: leftPos,
            });
          } else {
            let range = savedRange.current;
            if (!range) {
              const selection = window.getSelection();
              if (
                selection &&
                selection.rangeCount > 0 &&
                editorRef.current.contains(selection.anchorNode)
              ) {
                range = selection.getRangeAt(0);
              } else {
                range = document.createRange();
                range.selectNodeContents(editorRef.current);
                range.collapse(false);
              }
            }

            if (range) {
              const rect = range.getBoundingClientRect();
              if (rect.width === 0 && rect.height === 0) {
                const editorRect = editorRef.current.getBoundingClientRect();
                setPosition({
                  top: editorRect.bottom,
                  left: editorRect.left,
                });
              } else {
                const editorPosition =
                  editorRef.current.getBoundingClientRect();
                const shouldPlaceAbove =
                  editorPosition.bottom > window.innerHeight / 2;

                if (shouldPlaceAbove) {
                  setPosition({
                    bottom: window.innerHeight - rect.top,
                    left: rect.left,
                  });
                } else {
                  setPosition({
                    top: rect.bottom,
                    left: rect.left,
                  });
                }
              }
            }
          }

          if (availableTools) {
            setFilteredTools(availableTools);
          }
          setIsMenuOpen(true);
          setMenuType("tools");
          setSearchQuery("");
          setTool(null);
        }
      },
    }));



    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          isMenuOpen &&
          menuRef.current &&
          !menuTrigger.current?.element.contains(event.target as Node) &&
          !menuRef.current.contains(event.target as Node)
        ) {
          closeMenu();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isMenuOpen, closeMenu]);

    useEffect(() => {
      const cleanupEditor = () => {
        if (!editorRef.current) return;
        const content = editorRef.current.innerHTML.trim();

        if (content === "<br>" || content === "<br/>" || content === "") {
          editorRef.current.innerHTML = "";
          setIsMenuOpen(false);
          setMenuType("tools");
          setSearchQuery("");
          setTool(null);
        }
      };
      const editor = editorRef.current;
      if (editor) {
        editor.addEventListener("input", cleanupEditor);
        return () => editor.removeEventListener("input", cleanupEditor);
      }
    }, []);

    const isArabic = (text: string) => {
      if (!text) return false;
      const arabicRegex =
        /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
      const matches = text.match(arabicRegex);
      if (!matches) return false;

      const arabicCount = matches.length;
      const totalLetters = text.replace(/\s+/g, "").length || 1;
      return arabicCount / totalLetters >= 0.5;
    };

    const handleInput = () => {
      if (isComposingRef.current) return;
      saveSelection();

      const selection = window.getSelection();
      if (selection) {
        if (selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;

        if (textNode.nodeType !== Node.TEXT_NODE) return;

        const textContent = textNode.textContent;

        const cursorPosition = range.startOffset;
        const textBeforeCursor = textContent?.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor?.lastIndexOf("@");

        if (typeof lastAtIndex !== "undefined" && lastAtIndex > -1) {
          const query = textBeforeCursor?.substring(lastAtIndex + 1);
          if (!query?.includes(" ")) {
            setSearchQuery(query || "");
            if (query) {
              setMenuType("collections");
            }
            if (!query) {
              if (tool) {
                setMenuType("collections");
              } else {
                setMenuType("tools");
              }
            }
            if (!query && availableTools) {
              setFilteredTools(availableTools);
            }

            if (editorRef.current) {
              const rect = range.getBoundingClientRect();
              const editorPosition = editorRef.current.getBoundingClientRect();

              const shouldPlaceAbove =
                editorPosition.bottom > window.innerHeight / 2;

              if (shouldPlaceAbove) {
                setPosition({
                  bottom: window.innerHeight - rect.top,
                  left: rect.left,
                });
              } else {
                setPosition({
                  top: rect.bottom,
                  left: rect.left,
                });
              }
            }

            setIsMenuOpen(true);
            return;
          }
        }
      }
      setIsMenuOpen(false);
      setMenuType("tools");
      setTool(null);
      setSearchQuery("");
    };
    const insertMention = (mention: Mention, isEligible: () => boolean, sourceKey: QueryKey): void => {
      if (!isEditorCurrent() || !isEligible()) return;
      const selection: Selection | null = window.getSelection();
      let range: Range | null = null;

      if (
        selection &&
        selection.rangeCount > 0 &&
        editorRef.current?.contains(selection.anchorNode)
      ) {
        range = selection.getRangeAt(0);
      } else if (savedRange.current) {
        range = savedRange.current;
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      if (!range) {
        return;
      }

      const textNode: Node = range.startContainer;

      if (textNode.nodeType !== Node.TEXT_NODE) {
        if (range.startContainer === editorRef.current) {
          if (editorRef.current?.innerHTML === "") {
            editorRef.current.innerHTML = " ";
            const newRange = document.createRange();
            newRange.selectNodeContents(editorRef.current);
            newRange.collapse(false);
            range = newRange;
          }
        }
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
          const textNode = document.createTextNode(" ");
          range.insertNode(textNode);
          range.setStart(textNode, 0);
          range.setEnd(textNode, 0);
        } else {
          return;
        }
      }

      const currentTextNode =
        range.startContainer.nodeType === Node.TEXT_NODE
          ? range.startContainer
          : (range.startContainer.childNodes[range.startOffset] as Node);

      if (!currentTextNode || currentTextNode.nodeType !== Node.TEXT_NODE) {
        return;
      }

      const textContent: string = currentTextNode.textContent || "";
      const cursorPosition: number = range.startOffset;
      const textBeforeCursor: string = textContent.substring(0, cursorPosition);
      const lastAtIndex: number = textBeforeCursor.lastIndexOf("@");

      let beforeAt = textContent.substring(0, cursorPosition);
      let afterCursor = textContent.substring(cursorPosition);

      if (lastAtIndex !== -1) {
        beforeAt = textContent.substring(0, lastAtIndex);
      }

      currentTextNode.textContent = beforeAt;

      // Create pill element
      const pillWrapper: HTMLSpanElement = document.createElement("span");
      // Keep a selected result observable for the lifetime of the draft. Merely
      // closing the picker must not let cache GC invalidate a valid mention.
      const selectionKey = JSON.stringify(sourceKey);
      if (!retainedSelections.current.has(selectionKey)) {
        const observer = new QueryObserver(queryClient, { queryKey: sourceKey, enabled: false });
        retainedSelections.current.set(selectionKey, observer.subscribe(() => {}));
      }
      mentionEligibility.current.set(pillWrapper, isEligible);
      pillWrapper.dataset.mentionToolId = String(mention.toolId);
      pillWrapper.dataset.mentionToolName = mention.toolName;
      pillWrapper.dataset.mentionCollectionId = mention.collectionId ?? "";
      pillWrapper.dataset.mentionCollectionName = mention.collectionName ?? "";
      pillWrapper.dataset.mentionType = mention.type ?? "tool";
      pillWrapper.dataset.mentionMcpConnectionId = mention.mcpConnectionId ?? "";

      pillWrapper.contentEditable = "false";
      pillWrapper.style.userSelect = "none";
      pillWrapper.style.display = "inline-flex";
      pillWrapper.style.verticalAlign = "middle";
      pillWrapper.className = `inline-flex items-center  underline text-[var(--color-99)]  font-medium`;

      // Text - show only collection name if exists
      const nameSpan: HTMLSpanElement = document.createElement("span");
      nameSpan.className = "font-medium";

      if (mention.collectionName) {
        nameSpan.textContent = `${
          mention.type === "tool" ? mention.toolName + ":" : ""
        }${mention.collectionName}`;
      } else {
        nameSpan.textContent = mention.toolName;
      }

      // icon
      let strIcon = '<i data-lucide="globe"></i>';
      strIcon = renderToStaticMarkup(
        <ToolIcon
          name={mention.icon}
          style={{ color: "var(--color-99)" }}
          className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1"
        />,
      );

      const el = document.createElement("div");
      el.innerHTML = strIcon;
      const icon = el.firstElementChild;

      // Remove button
      // const removeBtn: HTMLButtonElement = document.createElement('button');
      // removeBtn.innerHTML = '×';
      // removeBtn.className = 'hover:bg-black/10 rounded-full p-0.5 transition-colors';
      // removeBtn.onmousedown = (e: MouseEvent): void => {
      //   e.preventDefault();

      //   pillWrapper.remove();
      //   editorRef.current?.focus();
      // };

      pillWrapper.appendChild(icon!);
      pillWrapper.appendChild(nameSpan);
      // pillWrapper.appendChild(removeBtn);

      // Insert into DOM
      const afterNode: Text = document.createTextNode(" " + afterCursor);
      const parentNode: Node | null = currentTextNode.parentNode;

      if (!parentNode) {
        return;
      }

      parentNode.insertBefore(pillWrapper, currentTextNode.nextSibling);
      parentNode.insertBefore(afterNode, pillWrapper.nextSibling);

      // Move cursor
      const newRange: Range = document.createRange();
      newRange.setStart(afterNode, 1);
      newRange.collapse(true);
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(newRange);
      }

      setIsMenuOpen(false);
      setMenuType("tools");
      setSearchQuery("");

      editorRef.current?.focus();
      setFocusPickerSearch(false);
      menuTrigger.current = null;
    };

    const handleToolSelect = async (tool: ITool) => {
      if (!isMenuItemSelectable(tool)) return;
      const isEligible = () => isEditorCurrent() && isComposerItemCurrent(queryClient, toolsQuery.queryKey, tool);
      if (!isEligible()) return;
      // MCP connections aren't mentionable themselves — drill into their tools.
      if (isMcpConnection(tool)) {
        setMenuType("collections");
        setTool(tool);
        setSearchQuery("");
        return;
      }
      if ("has_autocomplete" in tool && tool.has_autocomplete) {
        setMenuType("collections");
        setTool(tool);
        return;
      }
      insertMention({
        toolId: tool.id,
        toolName: tool.name,
        type: "tool",
        icon: (tool as any).icon || "",
        color: "bg-red-100 text-red-700 border-red-300",
      }, isEligible, toolsQuery.queryKey);
    };
    const handleCollectionSelect = async (
      collection: ICollection | IAutocompleteItem | IMcpTool,
    ) => {
      if (!isMenuItemSelectable(collection)) return;
      const selectedTool = tool;
      const sourceKey = selectedTool ? collectionsQuery.queryKey : autocompleteQuery.queryKey;
      const isEligible = () => {
        if (!isEditorCurrent()) return false;
        if (selectedTool && !isComposerItemCurrent(queryClient, toolsQuery.queryKey, selectedTool)) return false;
        if (isMcpConnection(selectedTool)) {
          const currentTools = queryClient.getQueryData<Tools>(toolsQuery.queryKey);
          return !!currentTools?.find(item => item.id === selectedTool?.id)?.tools?.some(item => item.id === collection.id);
        }
        return isComposerItemCurrent(queryClient, sourceKey, collection);
      };
      if (!isEligible()) return;
      // A tool exposed by an MCP connection: mention it directly as @tool_name.
      if (isMcpConnection(tool)) {
        const mcpTool = collection as IMcpTool;
        insertMention({
          toolId: mcpTool.id,
          toolName: mcpTool.name,
          type: "tool",
          mcpConnectionId:
            tool?.source_metadata?.mcp_connection_id ??
            mcpTool.source_metadata?.mcp_connection_id ??
            tool?.id,
          icon: mcpTool.icon || tool?.icon || "blocks",
          color: "bg-blue-100 text-blue-700 border-blue-300",
        }, isEligible, toolsQuery.queryKey);
        return;
      }
      if (tool) {
        insertMention({
          toolId: tool.id,
          toolName: tool.name,
          collectionId: collection.id,
          collectionName: collection.name,
          type: tool.type,
          icon: tool.icon,
          color: "bg-blue-100 text-blue-700 border-blue-300",
        }, isEligible, sourceKey);
      } else if ("type" in collection) {
        const isFlow = collection.type === "flow";
        insertMention({
          toolId: collection.type,
          toolName: collection.name,
          collectionId: collection.id,
          collectionName: collection.name,
          type: collection.type,
          icon: isFlow
            ? "workflow"
            : collection.type === "collection"
              ? "library-big"
              : "file-text",
          color: "bg-blue-100 text-blue-700 border-blue-300",
        }, isEligible, sourceKey);
      }
    };
    const extractContentForApi = (): {
      text: string;
      formattedText: string;
      mentions: Mention[];
    } => {
      if (!editorRef.current) {
        return { text: "", formattedText: "", mentions: [] };
      }

      let plainText: string = "";
      let formattedText: string = "";
      const mentions: Mention[] = [];

      const processNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || "";
          plainText += text;
          formattedText += text;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const tagName = element.tagName.toLowerCase();

          if (tagName === "br") {
            plainText += "\n";
            formattedText += "\n";
            return;
          }

          const toolId = element.dataset.mentionToolId;
          const toolName = element.dataset.mentionToolName;
          const collectionId = element.dataset.mentionCollectionId;
          const collectionName = element.dataset.mentionCollectionName;
          const mcpConnectionId = element.dataset.mentionMcpConnectionId;
          if (toolId && toolName) {
            const mention: Mention = {
              toolId,
              toolName,
              collectionId: collectionId || undefined,
              collectionName: collectionName || undefined,
              mcpConnectionId: mcpConnectionId || undefined,
              type: element.dataset.mentionType || "tool",
              icon: "",
              color: "",
            };
            mentions.push(mention);

            // Format as @tool_name[id] or @tool_name
            const formatted = formatMentionForApi(mention);
            formattedText += formatted;
            plainText += collectionName || toolName;
          } else {
            const isBlock = [
              "div",
              "p",
              "li",
              "h1",
              "h2",
              "h3",
              "h4",
              "h5",
              "h6",
              "ul",
              "ol",
              "blockquote",
              "section",
              "article",
            ].includes(tagName);

            if (isBlock) {
              if (plainText.length > 0 && !plainText.endsWith("\n")) {
                plainText += "\n";
                formattedText += "\n";
              }
            }

            element.childNodes.forEach(processNode);

            if (isBlock) {
              if (plainText.length > 0 && !plainText.endsWith("\n")) {
                plainText += "\n";
                formattedText += "\n";
              }
            }
          }
        }
      };

      editorRef.current.childNodes.forEach(processNode);

      return {
        text: plainText.trim(),
        formattedText: formattedText.trim(),
        mentions,
      };
    };
    const handleSend = async (): Promise<void> => {
      if (isStreaming || isUploading || isSubmitDisabled || isComposingRef.current || !isEditorCurrent()) return;
      const mentions = editorRef.current?.querySelectorAll<HTMLElement>("[data-mention-tool-id]");
      if (mentions && Array.from(mentions).some(element => !mentionEligibility.current.get(element)?.())) return;
      const content = extractContentForApi();

      if (content.text || content.mentions.length > 0) {
        onTextChange(content.formattedText);
        handleFormSubmit(content.formattedText, content.mentions);
        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
        releaseSelections();
      }
    };
    const handleStop = () => {
      if (!isStreaming || !stop || !isEditorCurrent()) return;
      stop();
    };
    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (
      e,
    ): void => {
      // IME confirmation belongs to the editor, including WebKit's legacy 229
      // Enter event, not to either mention selection or message submission.
      if (isComposingRef.current || e.nativeEvent.isComposing ||
        (e.key === "Enter" && e.nativeEvent.keyCode === 229)) return;
      if (isMenuOpen) {
        const currentItems = pickerItems;
        const selectableIndexes = currentItems.flatMap((item, index) => isMenuItemSelectable(item) ? [index] : []);

        if (e.key === "ArrowDown") {
          if (!selectableIndexes.length) return;
          e.preventDefault();
          setSelectedIndex(prev => selectableIndexes.find(index => index > prev) ?? selectableIndexes[0]);
          return;
        }
        if (e.key === "ArrowUp") {
          if (!selectableIndexes.length) return;
          e.preventDefault();
          setSelectedIndex(prev => [...selectableIndexes].reverse().find(index => index < prev) ?? selectableIndexes[selectableIndexes.length - 1]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          closeMenu(true);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          const selectedItem = currentItems?.[selectedIndex];
          if (e.key === "Tab" && (!selectedItem || !isMenuItemSelectable(selectedItem))) {
            closeMenu();
            return;
          }
          e.preventDefault();
          if (selectedItem && isMenuItemSelectable(selectedItem)) {
            if (menuType === "tools" && !searchQuery) {
              handleToolSelect(selectedItem as any);
            } else {
              handleCollectionSelect(selectedItem as any);
            }
          }
          return;
        }
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    return (
      <div className="relative">
        <div
          suppressContentEditableWarning
          role="textbox"
          aria-label={t("playground.messageInputLabel")}
          aria-multiline="true"
          aria-invalid={invalid || undefined}
          aria-describedby={errorMessageId}
          data-placeholder={t("playground.askSearchPlaceholder")}
          style={{
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          ref={editorRef}
          onInput={handleInput}
          contentEditable
          onKeyUp={saveSelection}
          onClick={saveSelection}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => { isComposingRef.current = true; }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            handleInput();
          }}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
          }}
          className={compactDesktop ? "resize-none rounded-md px-1 py-1.5 [padding-inline-end:3rem] min-h-9 max-h-[200px] overflow-auto bg-[var(--card-background)] text-black dark:text-white focus:outline-hidden focus:ring-2 focus:ring-primary text-base transition-all duration-200" : `resize-none rounded-md px-3 py-3 [padding-inline-end:4rem] sm:px-4 sm:py-4 sm:[padding-inline-end:5rem]
          bg-[var(--card-background)] text-black dark:text-white placeholder-gray-400
          focus:outline-hidden focus:ring-2 focus:ring-primary text-sm sm:text-base
          transition-all duration-200 min-h-[48px] sm:min-h-[56px] max-h-[160px] sm:max-h-[200px] overflow-scroll`}
        />
        <Button
          type="button"
          size="icon"
          aria-label={t(isStreaming ? "playground.stopGeneration" : "playground.sendMessage")}
          onClick={isStreaming ? handleStop : handleSend}
          disabled={!session.ready || editorSession !== session.token ||
            (isStreaming ? !stop : Boolean(isUploading || isSubmitDisabled))}
          style={{
            "--composer-control-border": primaryButton.borderColor[mode],
            "--composer-control-hover-bg": primaryButton.hoverBackgroundColor[mode],
            "--composer-control-hover-text": primaryButton.hoverTextColor[mode],
            "--composer-control-hover-border": primaryButton.hoverBorderColor[mode],
          } as CSSProperties}
          className={`
              absolute ${compactDesktop ? "end-1 top-0 size-9 min-w-9 rounded-lg" : "[inset-inline-end:0.4rem] bottom-[8px] sm:bottom-auto sm:top-[15%] h-[36px] sm:h-[40px] rounded-[5px] w-[36px] min-w-[36px] min-[600px]:w-[64px] min-[600px]:min-w-[64px]"} px-0 py-[6px]
              border border-[var(--composer-control-border)] shadow-none transition-all duration-200 ease-in-out
              hover:bg-[var(--composer-control-hover-bg)] hover:text-[var(--composer-control-hover-text)] hover:border-[var(--composer-control-hover-border)] hover:-translate-y-px
              active:bg-[var(--composer-control-hover-bg)] active:translate-y-0 disabled:opacity-70
            `}
        >
          {isStreaming ? (
            <Spinner className="motion-reduce:animate-none" aria-hidden="true" role="presentation" />
          ) : (
            <SendIcon className="size-6 rtl:scale-x-[-1]" />
          )}
        </Button>
        <div ref={menuRef}>
          <MenuSelection
            autoFocusSearch={focusPickerSearch}
            id={menuId ?? defaultMenuId}
            onSelect={handleToolSelect}
            isOpen={isMenuOpen && session.ready && editorSession === session.token}
            position={position}
            tools={filteredTools}
            onOpenChange={(open, restoreFocus) => { if (!open) closeMenu(restoreFocus); }}
            onSelectedIndexChange={setSelectedIndex}
            isError={pickerIsError}
            isItemSelectable={isMenuItemSelectable}
            onRetry={async () => {
              if (!isEditorCurrent()) return;
              const token = session.token;
              const focus = document.activeElement;
              const query = toolsQuery.isError || isMcpConnection(tool) ? toolsQuery : tool ? collectionsQuery : searchQuery ? autocompleteQuery : toolsQuery;
              await query.refetch();
              if (isEditorCurrent() && currentPicker.current.token === token && currentPicker.current.open &&
                (document.activeElement === focus || document.activeElement === document.body)) {
                menuRef.current?.querySelector<HTMLInputElement>("input")?.focus();
              }
            }}
            collections={
              isMcpConnection(tool)
                ? mcpToolItems
                : tool
                  ? readyCollectionsData
                  : readyAutocompleteData
            }
            menuType={menuType}
            onCollectionSelect={handleCollectionSelect}
            isLoading={pickerIsLoading}
            selectedIndex={selectedIndex}
            searchQuery={searchQuery}
            onSearchChange={(query) => {
              if (!isEditorCurrent()) return;
              setSearchQuery(query);
              // This used to run in the request's .then callback. Keep it in
              // the current editor event so late results cannot switch menus.
              if (!tool) setMenuType(query ? "collections" : "tools");
            }}
            selectedTool={menuType !== "tools" ? tool! : undefined}
          />
        </div>

        <style>{`
        [contentEditable=true]:empty:before {
          content: attr(data-placeholder);
          color: var(--input-placeholder);
          pointer-events: none;
        }
      `}</style>
      </div>
    );
  },
);
